import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { api } from "../lib/api";
import { Loader2, ArrowRight, CheckCircle2, Info } from "lucide-react";

const SERVICE_IMAGES = {
  incarcerated: "https://static.prod-images.emergentagent.com/jobs/af2e83e9-d6a3-45d6-aa2f-a0c7accadbc5/images/8889016c56b76ced3bdfc4b5346951572c56f691d0410c3d40251b66250400b3.png",
  families: "https://static.prod-images.emergentagent.com/jobs/af2e83e9-d6a3-45d6-aa2f-a0c7accadbc5/images/8504933cb4a945c2e359223bc71b66684bee78af7e00c995ea11c26804ffbd5e.png",
  defense: "https://static.prod-images.emergentagent.com/jobs/af2e83e9-d6a3-45d6-aa2f-a0c7accadbc5/images/a3ccaeadd59094419739dad97a9849501aa91053e3baf850a967f652b33a6ea1.png",
  hearings: "https://static.prod-images.emergentagent.com/jobs/af2e83e9-d6a3-45d6-aa2f-a0c7accadbc5/images/eb11c5a74c37c1a74729efcc681e06ab168bf991ee3602a17dcad76578a77443.png",
};

const SUPPORTING = {
  incarcerated: [
    "Attorney-graded 440 motions, Article 78 petitions, and appeals",
    "Clear, lawful communication with family and counsel",
    "Document preparation tuned to your specific situation",
    "Structured case timelines and evidence indexing",
    "Compassionate, confidential handling at every step",
  ],
  families: [
    "Plain-language explanations of every stage",
    "Document gathering, preparation, and filing assistance",
    "Coordination with your loved one and their legal team",
    "Regular, structured updates so you're never in the dark",
  ],
  defense: [
    "Attorney-reviewed pro-se documents — 440s, Article 78s, appeals",
    "Trial-ready document organization and exhibit indexing",
    "Discovery review and concise written summaries",
    "Witness outlines and hearing binders for your attorney",
    "Seamless handoff — your attorney focuses on strategy",
  ],
  hearings: [
    "Complete documentation package, indexed and ready",
    "Persuasive, chronological case timelines",
    "Mock prep and final-stage review checklists",
    "Witness coordination and exhibit logistics",
    "Support for disciplinary hearings and Tier 3 appeals",
  ],
};

const DOC_TYPES_BY_SERVICE = {
  incarcerated: ["440 motions", "Article 78 petitions", "Appeals", "Disciplinary hearings", "Tier 2 appeals", "Tier 3 appeals", "Professional grievances", "Parole packets"],
  defense: ["440 motions", "Article 78 petitions", "Appeals", "Briefs", "Motions"],
};

// Plain-English explanations shown when a user taps/clicks a document-type chip.
// These are designed for laypeople — accurate but never legal advice.
const DOC_TYPE_INFO = {
  "440 motions": {
    title: "440 Motion",
    body: "A post-conviction motion under New York CPL § 440.10 that asks the trial court to vacate a judgment based on issues like newly discovered evidence, ineffective counsel, or constitutional violations. We help organize the record and prepare attorney-graded pro-se filings.",
  },
  "Article 78 petitions": {
    title: "Article 78 Petition",
    body: "A civil proceeding used to challenge a decision or action by a New York State agency (parole, DOCCS, ALJ rulings, etc.). It is filed in Supreme Court within strict deadlines. We assist with structuring the record, exhibits, and pro-se petition drafts.",
  },
  "Appeals": {
    title: "Appeals",
    body: "A formal request that a higher court review the trial court's decision for legal error. We help build the record on appeal, organize exhibits, prepare timelines, and draft pro-se briefs ready for attorney review.",
  },
  "Disciplinary hearings": {
    title: "Disciplinary Hearings",
    body: "Internal facility hearings (Tier I, II, or III) where incarcerated individuals face disciplinary charges. We help prepare statements, gather witness lists, organize evidence, and structure your defense for the hearing officer.",
  },
  "Tier 3 appeals": {
    title: "Tier 3 Appeals",
    body: "The most serious tier of facility discipline — Tier 3 dispositions can affect time, housing, and parole. We support administrative appeals within DOCCS and, where applicable, prepare the record for Article 78 review in court.",
  },
  "Tier 2 appeals": {
    title: "Tier 2 Appeals",
    body: "Mid-tier facility disciplinary hearings under DOCCS Directive #4932. Tier 2 dispositions can result in loss of privileges, keeplock, and other sanctions that may impact your record. We help prepare the administrative appeal to the Superintendent — gathering documentation, structuring the legal and procedural arguments, and preserving the record for any subsequent Article 78 review.",
  },
  "Professional grievances": {
    title: "Professional Grievances",
    body: "Formal complaints filed through the DOCCS Incarcerated Individual Grievance Program (Directive #4040) regarding staff misconduct, conditions of confinement, medical care, or policy violations. We help draft the grievance, gather supporting documentation, and prepare CORC appeals when initial decisions are denied.",
  },
  "Parole packets": {
    title: "Parole Packets",
    body: "A comprehensive presentation prepared for the Parole Board ahead of an interview or appearance — including a release plan, programmatic accomplishments, character letters, employment and housing commitments, and a personal statement. A well-organized packet can meaningfully strengthen how your case is presented.",
  },
  "Briefs": {
    title: "Briefs",
    body: "Written arguments submitted to the court that lay out the facts, applicable law, and reasoning for the relief requested. We help research, structure, and draft attorney-graded pro-se briefs.",
  },
  "Motions": {
    title: "Motions",
    body: "Formal requests asking the court to issue a specific ruling — to suppress evidence, dismiss a charge, compel discovery, and more. We help prepare the supporting affidavits, exhibits, and memoranda.",
  },
};

export default function ServicePage({ serviceKey }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/services/${serviceKey}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [serviceKey]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
      </div>
    );
  }

  if (!data) return <div className="container-pl py-20">Service not found.</div>;

  return (
    <div data-testid={`service-page-${serviceKey}`}>
      {/* Header */}
      <section className="border-b border-zinc-800">
        <div className="container-pl py-20 lg:py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="overline mb-6" data-testid="service-overline">{data.tagline}</p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light text-zinc-50 leading-[1.05] mb-6">
                {data.title}
              </h1>
              <p className="text-base lg:text-lg text-zinc-300 leading-relaxed max-w-2xl mb-8">
                {data.description}
              </p>
              {DOC_TYPES_BY_SERVICE[serviceKey] && (
                <div className="flex flex-wrap gap-2 mb-8" data-testid={`doc-types-${serviceKey}`}>
                  {DOC_TYPES_BY_SERVICE[serviceKey].map((t) => {
                    const info = DOC_TYPE_INFO[t];
                    return (
                      <Popover key={t}>
                        <PopoverTrigger asChild>
                          <button
                            type="button"
                            className="group inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-400 border border-amber-400/40 rounded-sm bg-blue-950/30 hover:bg-amber-400/10 hover:border-amber-400 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400/50 cursor-pointer"
                            data-testid={`doc-chip-${t.replace(/\s+/g, "-").toLowerCase()}`}
                            aria-label={`Learn about ${t}`}
                          >
                            <span>{t}</span>
                            <Info className="h-3 w-3 opacity-70 group-hover:opacity-100" strokeWidth={2} />
                          </button>
                        </PopoverTrigger>
                        <PopoverContent
                          side="bottom"
                          align="start"
                          sideOffset={8}
                          className="w-72 bg-zinc-950 border border-amber-400/40 text-zinc-100 p-4 rounded-sm shadow-2xl"
                          data-testid={`doc-popover-${t.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          <p className="font-mono text-[10px] tracking-[0.22em] text-amber-400 uppercase mb-1">
                            Quick explainer
                          </p>
                          <h4 className="display-serif text-lg text-zinc-50 leading-tight mb-2">
                            {info?.title || t}
                          </h4>
                          <p className="text-xs leading-relaxed text-zinc-300">
                            {info?.body || "Detailed information coming soon."}
                          </p>
                          <p className="text-[10px] text-zinc-500 mt-3 italic">
                            Informational only — not legal advice.
                          </p>
                        </PopoverContent>
                      </Popover>
                    );
                  })}
                </div>
              )}
              <Link to="/booking">
                <Button
                  size="lg"
                  className="rounded-sm bg-blue-700 hover:bg-blue-600 text-white px-7 h-12"
                  data-testid={`service-schedule-${serviceKey}`}
                >
                  Schedule a Consultation
                  <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                </Button>
              </Link>
            </div>
            <div className="lg:col-span-5">
              <div className="rounded-sm overflow-hidden border border-zinc-800 aspect-[4/3]">
                <img
                  src={SERVICE_IMAGES[serviceKey]}
                  alt={data.title}
                  className="w-full h-full object-cover"
                  data-testid={`service-image-${serviceKey}`}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services list */}
      <section className="section-pad bg-black border-b border-zinc-800">
        <div className="container-pl">
          <p className="overline mb-4">What we deliver</p>
          <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-zinc-50 mb-16 max-w-2xl">
            Specific, structured support — tailored to your situation.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-800 border border-zinc-800">
            {data.services.map((s, i) => (
              <div key={s.name} className="bg-zinc-950 p-8" data-testid={`service-item-${i}`}>
                <p className="font-mono text-xs text-amber-400 mb-4">0{i + 1}</p>
                <h3 className="text-xl font-medium text-zinc-50 mb-3">{s.name}</h3>
                <p className="text-sm text-zinc-300 leading-relaxed">{s.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supporting list */}
      <section className="section-pad border-b border-zinc-800">
        <div className="container-pl">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <p className="overline mb-4">Our standards</p>
              <h2 className="text-3xl sm:text-4xl tracking-tight font-medium text-zinc-50 mb-6">
                The details we never overlook.
              </h2>
              <p className="text-base text-zinc-300 leading-relaxed">
                Every engagement is a promise. These are the standards we hold ourselves to, on every matter.
              </p>
            </div>
            <div className="lg:col-span-7">
              <ul className="space-y-4">
                {SUPPORTING[serviceKey].map((point) => (
                  <li key={point} className="flex items-start gap-4 pb-4 border-b border-zinc-800 last:border-0" data-testid={`standard-${point.slice(0, 20)}`}>
                    <CheckCircle2 className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                    <p className="text-base text-zinc-200 leading-relaxed">{point}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-blue-700 text-white">
        <div className="container-pl py-16">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <h2 className="text-2xl sm:text-3xl font-light mb-2">Ready to talk?</h2>
              <p className="text-blue-100">Book a quick call. We'll listen and explain next steps.</p>
            </div>
            <Link to="/booking">
              <Button size="lg" className="rounded-sm bg-zinc-950 text-amber-400 hover:bg-blue-950/30 h-12 px-7">
                Schedule Now
                <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
